import React, { useState, useRef, useEffect } from 'react';
import { Responsible } from '../../../types';
import { KlFullModal } from '../modal/KlFullModal';

interface ApprovalStep {
    reviewerId: string;
    minAmount?: number;
}

interface ApprovalFlows {
    subcontracts: { reviewerId: string }[];
    paymentStatements: { reviewerId: string; minAmount?: number }[];
}

interface MobileApprovalConfigModalProps {
    open: boolean;
    onClose: () => void;
    approvalFlows: ApprovalFlows;
    setApprovalFlows: React.Dispatch<React.SetStateAction<ApprovalFlows>>;
    sortedResponsibles: Responsible[];
}

export const MobileApprovalConfigModal: React.FC<MobileApprovalConfigModalProps> = ({
    open,
    onClose,
    approvalFlows,
    setApprovalFlows,
    sortedResponsibles
}) => {
    const [activeMenuKey, setActiveMenuKey] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Cerrar menú contextual al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setActiveMenuKey(null);
            }
        };
        if (activeMenuKey) {
            document.addEventListener('pointerdown', handleClickOutside);
            return () => document.removeEventListener('pointerdown', handleClickOutside);
        }
    }, [activeMenuKey]);

    const handleAddSubReviewer = () => {
        setApprovalFlows(prev => {
            const updated = {
                ...prev,
                subcontracts: [...prev.subcontracts, { reviewerId: '' }]
            };
            try {
                localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(updated));
            } catch {}
            return updated;
        });
    };

    const handleAddPaymentReviewer = () => {
        setApprovalFlows(prev => {
            const updated = {
                ...prev,
                paymentStatements: [...prev.paymentStatements, { reviewerId: '' }]
            };
            try {
                localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(updated));
            } catch {}
            return updated;
        });
    };

    const handleRemoveSubReviewer = (index: number) => {
        setApprovalFlows(prev => {
            const updated = {
                ...prev,
                subcontracts: prev.subcontracts.filter((_, i) => i !== index)
            };
            try {
                localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(updated));
            } catch {}
            return updated;
        });
        setActiveMenuKey(null);
    };

    const handleRemovePaymentReviewer = (index: number) => {
        setApprovalFlows(prev => {
            const updated = {
                ...prev,
                paymentStatements: prev.paymentStatements.filter((_, i) => i !== index)
            };
            try {
                localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(updated));
            } catch {}
            return updated;
        });
        setActiveMenuKey(null);
    };

    const handleMoveSubReviewer = (index: number, direction: 'up' | 'down') => {
        setApprovalFlows(prev => {
            const list = [...prev.subcontracts];
            const targetIndex = direction === 'up' ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= list.length) return prev;
            const temp = list[index];
            list[index] = list[targetIndex];
            list[targetIndex] = temp;
            const updated = { ...prev, subcontracts: list };
            try {
                localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(updated));
            } catch {}
            return updated;
        });
        setActiveMenuKey(null);
    };

    const handleMovePaymentReviewer = (index: number, direction: 'up' | 'down') => {
        setApprovalFlows(prev => {
            const list = [...prev.paymentStatements];
            const targetIndex = direction === 'up' ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= list.length) return prev;
            const temp = list[index];
            list[index] = list[targetIndex];
            list[targetIndex] = temp;
            const updated = { ...prev, paymentStatements: list };
            try {
                localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(updated));
            } catch {}
            return updated;
        });
        setActiveMenuKey(null);
    };

    return (
        <KlFullModal
            open={open}
            onClose={onClose}
            title="Configuración de Aprobaciones"
            subtitle="Define los flujos de aprobación y asigna los revisores responsables"
            footer={
                <button
                    type="button"
                    onClick={onClose}
                    className="b b-p w-full"
                >
                    Listo
                </button>
            }
        >
            <div className="flex flex-col gap-3 p-3 pb-8">
                {/* Card 1: Aprobación Subcontratos */}
                <div className="card bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700">
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-[10px] bg-[#EFF6FF] dark:bg-blue-950/60 text-[#1D4ED8] dark:text-blue-300 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-[24px]">handshake</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100">
                                Aprobación Subcontratos
                            </h4>
                            <p className="text-[11px] text-[#64748B] dark:text-slate-400 leading-snug mt-0.5">
                                Flujo de firmas requeridas para activar un contrato de subcontratación.
                            </p>
                        </div>
                    </div>

                    {/* Lista de revisores de subcontratos */}
                    <div className="flex flex-col gap-2 pt-1">
                        {approvalFlows.subcontracts.length === 0 ? (
                            <div className="h-[72px] border-[1.5px] border-dashed border-[#CBD5E1] dark:border-slate-700 rounded-[12px] flex items-center justify-center text-[#64748B] text-[13px] italic bg-[#F8FAFC] dark:bg-slate-900/40">
                                No se han configurado revisores.
                            </div>
                        ) : (
                            approvalFlows.subcontracts.map((step, idx) => {
                                const menuKey = `sub-${idx}`;
                                const isMenuOpen = activeMenuKey === menuKey;

                                return (
                                    <div
                                        key={idx}
                                        className="min-h-[44px] bg-[#F8FAFC] dark:bg-slate-900/60 rounded-[10px] border border-[#E2E8F0] dark:border-slate-700/60 px-2.5 py-1.5 flex items-center gap-2 relative"
                                    >
                                        <span className="font-mono text-[12px] font-bold text-[#64748B] dark:text-slate-400 w-6 text-center shrink-0">
                                            {idx + 1}°
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <select
                                                value={step.reviewerId}
                                                onChange={(e) => {
                                                    const updated = [...approvalFlows.subcontracts];
                                                    updated[idx] = { ...updated[idx], reviewerId: e.target.value };
                                                    setApprovalFlows(prev => {
                                                        const res = { ...prev, subcontracts: updated };
                                                        try {
                                                            localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(res));
                                                        } catch {}
                                                        return res;
                                                    });
                                                }}
                                                className="w-full bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[8px] px-2 py-1.5 text-[13px] font-medium text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                                            >
                                                <option value="">Seleccionar revisor…</option>
                                                {sortedResponsibles.map(r => (
                                                    <option key={r.id} value={r.id}>
                                                        {r.name} ({r.role || 'Sin cargo'})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Menú ⋮ con acciones */}
                                        <div className="relative shrink-0">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveMenuKey(prev => (prev === menuKey ? null : menuKey));
                                                }}
                                                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                                                aria-label="Acciones de revisor"
                                            >
                                                <span className="material-symbols-outlined text-[20px]">more_vert</span>
                                            </button>

                                            {isMenuOpen && (
                                                <div
                                                    ref={menuRef}
                                                    className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-30 animate-in fade-in zoom-in duration-100"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {idx > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMoveSubReviewer(idx, 'up')}
                                                            className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px] text-slate-500">arrow_upward</span>
                                                            <span>Subir orden</span>
                                                        </button>
                                                    )}
                                                    {idx < approvalFlows.subcontracts.length - 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMoveSubReviewer(idx, 'down')}
                                                            className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px] text-slate-500">arrow_downward</span>
                                                            <span>Bajar orden</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveSubReviewer(idx)}
                                                        className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#DC2626] hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/60"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                                        <span>Quitar revisor</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}

                        {/* Botón Añadir Revisor */}
                        <button
                            type="button"
                            onClick={handleAddSubReviewer}
                            className="b b-t w-full h-10 rounded-[10px] text-[13px] font-bold flex items-center justify-center gap-1.5 mt-1"
                        >
                            <span className="material-symbols-outlined text-[18px]">add</span>
                            <span>Añadir revisor</span>
                        </button>
                    </div>
                </div>

                {/* Card 2: Aprobación de Estados de Pago */}
                <div className="card bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700">
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-[10px] bg-[#DCFCE7] dark:bg-green-950/60 text-[#166534] dark:text-green-300 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-[24px]">payments</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className="text-[13px] font-bold text-[#0F172A] dark:text-slate-100">
                                Aprobación de Estados de Pago
                            </h4>
                            <p className="text-[11px] text-[#64748B] dark:text-slate-400 leading-snug mt-0.5">
                                Flujo secuencial de aprobaciones necesario para liberar y visar un estado de pago.
                            </p>
                        </div>
                    </div>

                    {/* Lista de revisores de EP */}
                    <div className="flex flex-col gap-2 pt-1">
                        {approvalFlows.paymentStatements.length === 0 ? (
                            <div className="h-[72px] border-[1.5px] border-dashed border-[#CBD5E1] dark:border-slate-700 rounded-[12px] flex items-center justify-center text-[#64748B] text-[13px] italic bg-[#F8FAFC] dark:bg-slate-900/40">
                                No se han configurado revisores.
                            </div>
                        ) : (
                            approvalFlows.paymentStatements.map((step, idx) => {
                                const menuKey = `ep-${idx}`;
                                const isMenuOpen = activeMenuKey === menuKey;

                                return (
                                    <div
                                        key={idx}
                                        className="bg-[#F8FAFC] dark:bg-slate-900/60 rounded-[10px] border border-[#E2E8F0] dark:border-slate-700/60 p-2.5 flex flex-col gap-2 relative"
                                    >
                                        <div className="min-h-[44px] flex items-center gap-2">
                                            <span className="font-mono text-[12px] font-bold text-[#64748B] dark:text-slate-400 w-6 text-center shrink-0">
                                                {idx + 1}°
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <select
                                                    value={step.reviewerId}
                                                    onChange={(e) => {
                                                        const updated = [...approvalFlows.paymentStatements];
                                                        updated[idx] = { ...updated[idx], reviewerId: e.target.value };
                                                        setApprovalFlows(prev => {
                                                            const res = { ...prev, paymentStatements: updated };
                                                            try {
                                                                localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(res));
                                                            } catch {}
                                                            return res;
                                                        });
                                                    }}
                                                    className="w-full bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[8px] px-2 py-1.5 text-[13px] font-medium text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#16A34A]"
                                                >
                                                    <option value="">Seleccionar revisor…</option>
                                                    {sortedResponsibles.map(r => (
                                                        <option key={r.id} value={r.id}>
                                                            {r.name} ({r.role || 'Sin cargo'})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Menú ⋮ con acciones */}
                                            <div className="relative shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setActiveMenuKey(prev => (prev === menuKey ? null : menuKey));
                                                    }}
                                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                                                    aria-label="Acciones de revisor"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">more_vert</span>
                                                </button>

                                                {isMenuOpen && (
                                                    <div
                                                        ref={menuRef}
                                                        className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-1 z-30 animate-in fade-in zoom-in duration-100"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {idx > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleMovePaymentReviewer(idx, 'up')}
                                                                className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                                            >
                                                                <span className="material-symbols-outlined text-[18px] text-slate-500">arrow_upward</span>
                                                                <span>Subir orden</span>
                                                            </button>
                                                        )}
                                                        {idx < approvalFlows.paymentStatements.length - 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleMovePaymentReviewer(idx, 'down')}
                                                                className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#0F172A] dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                                                            >
                                                                <span className="material-symbols-outlined text-[18px] text-slate-500">arrow_downward</span>
                                                                <span>Bajar orden</span>
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemovePaymentReviewer(idx)}
                                                            className="w-full text-left px-3 py-2 text-[13px] font-semibold text-[#DC2626] hover:bg-red-50 dark:hover:bg-red-950/30 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/60"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                                            <span>Quitar revisor</span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Monto umbral condicional */}
                                        <div className="flex items-center gap-2 pl-8 pt-0.5">
                                            <span className="text-[11px] text-[#64748B] dark:text-slate-400 font-medium shrink-0">
                                                Aplica si monto supera:
                                            </span>
                                            <div className="relative flex-1 max-w-[170px]">
                                                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] text-[#94A3B8] font-bold">
                                                    $
                                                </span>
                                                <input
                                                    type="number"
                                                    placeholder="Siempre aplica"
                                                    value={step.minAmount ?? ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value ? parseFloat(e.target.value) : undefined;
                                                        const updated = [...approvalFlows.paymentStatements];
                                                        updated[idx] = { ...updated[idx], minAmount: val };
                                                        setApprovalFlows(prev => {
                                                            const res = { ...prev, paymentStatements: updated };
                                                            try {
                                                                localStorage.setItem('klydapp_approval_flows_v2', JSON.stringify(res));
                                                            } catch {}
                                                            return res;
                                                        });
                                                    }}
                                                    className="w-full bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 rounded-[6px] pl-6 pr-2 py-1 text-[11px] text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#16A34A]"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}

                        {/* Botón Añadir Revisor */}
                        <button
                            type="button"
                            onClick={handleAddPaymentReviewer}
                            className="b b-t w-full h-10 rounded-[10px] text-[13px] font-bold flex items-center justify-center gap-1.5 mt-1"
                        >
                            <span className="material-symbols-outlined text-[18px]">add</span>
                            <span>Añadir revisor</span>
                        </button>
                    </div>
                </div>
            </div>
        </KlFullModal>
    );
};
